import type { StyleProp, ViewStyle } from 'react-native';
import { StyleSheet } from 'react-native';

import { spacing } from '../../styles/constants';

type Spacings = keyof typeof spacing;

export interface SpacingProps {
  m?: Spacings;
  mx?: Spacings;
  my?: Spacings;
  mt?: Spacings;
  mr?: Spacings;
  mb?: Spacings;
  ml?: Spacings;
  p?: Spacings;
  px?: Spacings;
  py?: Spacings;
  pt?: Spacings;
  pr?: Spacings;
  pb?: Spacings;
  pl?: Spacings;
}

type Shorthand = keyof SpacingProps;

const shortHandToStyle: Record<Shorthand, (v: Spacings) => object> = {
  m: v => ({ margin: spacing[v] }),
  mx: v => ({ marginHorizontal: spacing[v] }),
  my: v => ({ marginVertical: spacing[v] }),
  mt: v => ({ marginTop: spacing[v] }),
  mr: v => ({ marginRight: spacing[v] }),
  mb: v => ({ marginBottom: spacing[v] }),
  ml: v => ({ marginLeft: spacing[v] }),

  p: v => ({ padding: spacing[v] }),
  px: v => ({ paddingHorizontal: spacing[v] }),
  py: v => ({ paddingVertical: spacing[v] }),
  pt: v => ({ paddingTop: spacing[v] }),
  pr: v => ({ paddingRight: spacing[v] }),
  pb: v => ({ paddingBottom: spacing[v] }),
  pl: v => ({ paddingLeft: spacing[v] }),
};

// statically create a stylesheet for each combination for performance
const shortHandStylesMap: Record<Shorthand, Record<Spacings, ViewStyle>> = Object.fromEntries(
  (Object.keys(shortHandToStyle) as Shorthand[]).map(key => {
    const getStyle = shortHandToStyle[key];
    const stylesForKey = Object.fromEntries(
      (Object.keys(spacing) as Spacings[]).map(spacingKey => [
        spacingKey,
        StyleSheet.create({ style: getStyle(spacingKey) }).style,
      ])
    ) as Record<Spacings, ViewStyle>;
    return [key, stylesForKey];
  })
) as Record<Shorthand, Record<Spacings, ViewStyle>>;

export const getStylesWithSpacing = (
  styles: StyleProp<ViewStyle>[],
  style: StyleProp<ViewStyle>,
  restProps: SpacingProps
) => {
  const { m, mx, my, mt, mr, mb, ml, p, px, py, pt, pr, pb, pl } = restProps;

  if (m != null) {
    styles.push(shortHandStylesMap.m[m]);
  }
  if (mx != null) {
    styles.push(shortHandStylesMap.mx[mx]);
  }
  if (my != null) {
    styles.push(shortHandStylesMap.my[my]);
  }
  if (mt != null) {
    styles.push(shortHandStylesMap.mt[mt]);
  }
  if (mr != null) {
    styles.push(shortHandStylesMap.mr[mr]);
  }
  if (mb != null) {
    styles.push(shortHandStylesMap.mb[mb]);
  }
  if (ml != null) {
    styles.push(shortHandStylesMap.ml[ml]);
  }

  if (p != null) {
    styles.push(shortHandStylesMap.p[p]);
  }
  if (px != null) {
    styles.push(shortHandStylesMap.px[px]);
  }
  if (py != null) {
    styles.push(shortHandStylesMap.py[py]);
  }
  if (pt != null) {
    styles.push(shortHandStylesMap.pt[pt]);
  }
  if (pr != null) {
    styles.push(shortHandStylesMap.pr[pr]);
  }
  if (pb != null) {
    styles.push(shortHandStylesMap.pb[pb]);
  }
  if (pl != null) {
    styles.push(shortHandStylesMap.pl[pl]);
  }

  // final style at end to maybe override any shorthands
  if (style) {
    styles.push(style);
  }

  return styles;
};
